import * as THREE from 'three';
import { CameraConfig, CameraMode, InputState, Vec2 } from '../types';
import { DEFAULT_CAMERA_CONFIG, INPUT_CONFIG, KEY_BINDINGS } from '../constants';
import { InputManager } from '../input/InputManager';

/**
 * Camera controller supporting orbit, fly, and follow modes.
 * Handles both desktop (mouse/keyboard) and mobile (touch) controls.
 */
export class CameraController {
  public camera: THREE.PerspectiveCamera;
  private config: CameraConfig;
  private inputManager: InputManager;

  // Orbit mode state
  private orbitTarget: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private spherical: THREE.Spherical = new THREE.Spherical();
  private orbitDamping: THREE.Vector3 = new THREE.Vector3();

  // Ship flight mode state
  private shipVelocity: THREE.Vector3 = new THREE.Vector3();
  private shipAngularVelocity: THREE.Vector3 = new THREE.Vector3();
  private throttle: number = 0;
  private maxSpeed: number = 200;
  private acceleration: number = 120;
  private rotationSpeed: number = 2.5;
  private dampingFactor: number = 0.995; // Very light drag in space
  private angularDamping: number = 0.92;

  // Touch control state
  private touchMovement = { forward: 0, strafe: 0 };
  private touchVertical: number = 0;
  private touchLookDelta = { x: 0, y: 0 };

  // Fly mode state (legacy)
  private flyVelocity: THREE.Vector3 = new THREE.Vector3();
  private flyRotation: THREE.Euler = new THREE.Euler(0, 0, 0, 'YXZ');

  // Interaction state
  private isRightMouseDown: boolean = false;
  private lastPointerPosition: Vec2 = { x: 0, y: 0 };
  private targetZoom: number;

  constructor(
    container: HTMLElement,
    inputManager: InputManager,
    config: Partial<CameraConfig> = {}
  ) {
    this.config = { ...DEFAULT_CAMERA_CONFIG, ...config };
    this.inputManager = inputManager;

    // Create camera
    const aspect = container.clientWidth / container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(
      this.config.fov,
      aspect,
      this.config.near,
      this.config.far
    );

    // Initialize orbit state
    this.spherical.radius = this.config.orbitDistance;
    this.spherical.phi = Math.PI / 3; // Elevation angle
    this.spherical.theta = 0; // Azimuth angle
    this.targetZoom = this.spherical.radius;

    this.updateCameraFromSpherical();
    this.bindInputEvents();
  }

  private bindInputEvents(): void {
    // Track right mouse button via native events
    const canvas = document.querySelector('canvas');
    if (canvas) {
      canvas.addEventListener('mousedown', (e) => {
        if (e.button === 2) this.isRightMouseDown = true;
      });
      canvas.addEventListener('mouseup', (e) => {
        if (e.button === 2) this.isRightMouseDown = false;
      });
      canvas.addEventListener('mouseleave', () => {
        this.isRightMouseDown = false;
      });
    }

    // Pointer down
    this.inputManager.on('pointerdown', (e) => {
      if (e.pointer) {
        const state = this.inputManager.getState();
        if (state.pointers.size === 1) {
          this.lastPointerPosition = { ...e.pointer.position };
        }
      }
    });

    // Wheel zoom
    this.inputManager.on('wheel', (e) => {
      if (this.config.mode === 'orbit' && e.scrollDelta !== undefined) {
        this.targetZoom *= 1 + e.scrollDelta * INPUT_CONFIG.wheelZoomSpeed;
        this.targetZoom = THREE.MathUtils.clamp(
          this.targetZoom,
          this.config.orbitMinDistance,
          this.config.orbitMaxDistance
        );
      }
    });

    // Pinch zoom
    this.inputManager.on('pinchmove', (e) => {
      if (this.config.mode === 'orbit' && e.pinchScale !== undefined) {
        // Inverse relationship - spread fingers to zoom in
        const zoomFactor = 1 / e.pinchScale;
        this.targetZoom = this.spherical.radius * zoomFactor;
        this.targetZoom = THREE.MathUtils.clamp(
          this.targetZoom,
          this.config.orbitMinDistance,
          this.config.orbitMaxDistance
        );
      }
    });
  }

  public update(deltaTime: number, input: InputState): void {
    switch (this.config.mode) {
      case 'ship':
        this.updateShipMode(deltaTime, input);
        break;
      case 'orbit':
        this.updateOrbitMode(deltaTime, input);
        break;
      case 'fly':
        this.updateFlyMode(deltaTime, input);
        break;
      case 'follow':
        this.updateFollowMode(deltaTime);
        break;
    }
  }

  private updateOrbitMode(deltaTime: number, input: InputState): void {
    // Handle rotation via single-finger/left-click drag
    if (input.isDragging && input.primaryPointer && !input.isPinching) {
      const delta = input.primaryPointer.delta;
      const pointerCount = input.pointers.size;

      // Single pointer = rotate, Two pointers without pinch = pan
      if (pointerCount === 1) {
        // Check if right mouse button for panning (desktop)
        if (this.isRightMouseDown) {
          // Right-click drag = pan
          this.panCamera(delta.x, delta.y);
        } else {
          // Left-click/single touch = rotate
          this.spherical.theta -= delta.x * this.config.orbitSpeed;
          this.spherical.phi -= delta.y * this.config.orbitSpeed;

          // Clamp phi to avoid flipping
          this.spherical.phi = THREE.MathUtils.clamp(
            this.spherical.phi,
            0.1,
            Math.PI - 0.1
          );
        }
      }
    }

    // Two-finger drag (not pinching) = pan on mobile
    if (input.pointers.size === 2 && !input.isPinching && input.primaryPointer) {
      const delta = input.primaryPointer.delta;
      this.panCamera(delta.x, delta.y);
    }

    // Keyboard pan (move target) - increased speed
    const panSpeed = this.config.flySpeed * deltaTime * 2;
    const forward = new THREE.Vector3();
    this.camera.getWorldDirection(forward);
    forward.y = 0;
    forward.normalize();

    const right = new THREE.Vector3();
    right.crossVectors(forward, this.camera.up).normalize();

    if (this.inputManager.isKeyDown(KEY_BINDINGS.forward)) {
      this.orbitTarget.add(forward.clone().multiplyScalar(panSpeed));
    }
    if (this.inputManager.isKeyDown(KEY_BINDINGS.backward)) {
      this.orbitTarget.add(forward.clone().multiplyScalar(-panSpeed));
    }
    if (this.inputManager.isKeyDown(KEY_BINDINGS.left)) {
      this.orbitTarget.add(right.clone().multiplyScalar(-panSpeed));
    }
    if (this.inputManager.isKeyDown(KEY_BINDINGS.right)) {
      this.orbitTarget.add(right.clone().multiplyScalar(panSpeed));
    }
    if (this.inputManager.isKeyDown(KEY_BINDINGS.up)) {
      this.orbitTarget.y += panSpeed;
    }
    if (this.inputManager.isKeyDown(KEY_BINDINGS.down)) {
      this.orbitTarget.y -= panSpeed;
    }

    // Smooth zoom
    this.spherical.radius = THREE.MathUtils.lerp(
      this.spherical.radius,
      this.targetZoom,
      1 - Math.pow(this.config.dampingFactor, deltaTime * 60)
    );

    this.updateCameraFromSpherical();
  }

  private isDesktopDragRotate(input: InputState): boolean {
    // Check if we should rotate - on desktop, check for modifier or right mouse
    // For simplicity, we rotate on any drag when using mouse
    if (input.pointers.size === 1) {
      const pointer = input.primaryPointer;
      if (pointer) {
        // If it's a touch device (pointerId > 1 typically for touch), rotate on drag
        // For mouse, we can check if it's a right-click or use keyboard modifier
        return this.inputManager.isKeyDown(['ShiftLeft', 'ShiftRight']) || true;
      }
    }
    return false;
  }

  private panCamera(deltaX: number, deltaY: number): void {
    // Pan the orbit target based on screen-space movement
    const panSpeed = this.spherical.radius * 0.002;

    // Get camera right and up vectors
    const right = new THREE.Vector3();
    const up = new THREE.Vector3();

    this.camera.getWorldDirection(up);
    right.crossVectors(up, this.camera.up).normalize();
    up.copy(this.camera.up);

    // Move orbit target
    this.orbitTarget.add(right.multiplyScalar(-deltaX * panSpeed));
    this.orbitTarget.add(up.multiplyScalar(deltaY * panSpeed));
  }

  private updateShipMode(deltaTime: number, input: InputState): void {
    // Get camera direction vectors
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion);

    // === ROTATION via mouse/touch drag - direct and responsive ===
    if (input.isDragging && input.primaryPointer && !input.isPinching) {
      const delta = input.primaryPointer.delta;

      // Direct rotation - no velocity accumulation for snappy feel
      const pitchAmount = -delta.y * 0.003;
      const yawAmount = -delta.x * 0.003;

      const pitchQuat = new THREE.Quaternion().setFromAxisAngle(right, pitchAmount);
      const yawQuat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yawAmount);

      this.camera.quaternion.premultiply(pitchQuat);
      this.camera.quaternion.premultiply(yawQuat);
    }

    // === TOUCH LOOK from virtual joystick (if used) ===
    if (this.touchLookDelta.x !== 0 || this.touchLookDelta.y !== 0) {
      const pitchAmount = -this.touchLookDelta.y * 0.02 * deltaTime;
      const yawAmount = -this.touchLookDelta.x * 0.02 * deltaTime;

      const pitchQuat = new THREE.Quaternion().setFromAxisAngle(right, pitchAmount);
      const yawQuat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yawAmount);

      this.camera.quaternion.premultiply(pitchQuat);
      this.camera.quaternion.premultiply(yawQuat);
    }

    // === THRUST controls - immediate response ===
    const thrustPower = this.acceleration * deltaTime;

    // W/Up = forward thrust
    if (this.inputManager.isKeyDown(KEY_BINDINGS.forward)) {
      this.shipVelocity.add(forward.clone().multiplyScalar(thrustPower));
    }
    // S/Down = reverse thrust
    if (this.inputManager.isKeyDown(KEY_BINDINGS.backward)) {
      this.shipVelocity.add(forward.clone().multiplyScalar(-thrustPower * 0.6));
    }

    // === STRAFE controls ===
    // A/Left = strafe left
    if (this.inputManager.isKeyDown(KEY_BINDINGS.left)) {
      this.shipVelocity.add(right.clone().multiplyScalar(-thrustPower * 0.7));
    }
    // D/Right = strafe right
    if (this.inputManager.isKeyDown(KEY_BINDINGS.right)) {
      this.shipVelocity.add(right.clone().multiplyScalar(thrustPower * 0.7));
    }
    // E/Space = thrust up
    if (this.inputManager.isKeyDown(KEY_BINDINGS.up)) {
      this.shipVelocity.add(up.clone().multiplyScalar(thrustPower * 0.7));
    }
    // Q/Shift = thrust down
    if (this.inputManager.isKeyDown(KEY_BINDINGS.down)) {
      this.shipVelocity.add(up.clone().multiplyScalar(-thrustPower * 0.7));
    }

    // === TOUCH MOVEMENT from virtual joystick ===
    if (this.touchMovement.forward !== 0) {
      this.shipVelocity.add(forward.clone().multiplyScalar(thrustPower * this.touchMovement.forward));
    }
    if (this.touchMovement.strafe !== 0) {
      this.shipVelocity.add(right.clone().multiplyScalar(thrustPower * this.touchMovement.strafe * 0.7));
    }
    if (this.touchVertical !== 0) {
      this.shipVelocity.add(up.clone().multiplyScalar(thrustPower * this.touchVertical * 0.7));
    }

    // === BRAKE - B key to quickly slow down ===
    if (this.inputManager.isKeyDown(['KeyB'])) {
      this.shipVelocity.multiplyScalar(0.95);
    }

    // === ROLL controls ===
    if (this.inputManager.isKeyDown(['KeyZ'])) {
      const rollQuat = new THREE.Quaternion().setFromAxisAngle(forward, this.rotationSpeed * deltaTime);
      this.camera.quaternion.premultiply(rollQuat);
    }
    if (this.inputManager.isKeyDown(['KeyX'])) {
      const rollQuat = new THREE.Quaternion().setFromAxisAngle(forward, -this.rotationSpeed * deltaTime);
      this.camera.quaternion.premultiply(rollQuat);
    }

    // Clamp velocity to max speed
    const currentSpeed = this.shipVelocity.length();
    if (currentSpeed > this.maxSpeed) {
      this.shipVelocity.multiplyScalar(this.maxSpeed / currentSpeed);
    }

    // Very light space drag - ships drift in space
    this.shipVelocity.multiplyScalar(this.dampingFactor);

    // Apply velocity to position
    this.camera.position.add(this.shipVelocity.clone().multiplyScalar(deltaTime));

    // Normalize quaternion to prevent drift
    this.camera.quaternion.normalize();
  }

  private updateFlyMode(deltaTime: number, input: InputState): void {
    const speed = this.config.flySpeed;

    // Get camera directions
    const forward = new THREE.Vector3();
    this.camera.getWorldDirection(forward);

    const right = new THREE.Vector3();
    right.crossVectors(forward, this.camera.up).normalize();

    const up = this.camera.up.clone();

    // Calculate movement input
    const moveInput = new THREE.Vector3();

    if (this.inputManager.isKeyDown(KEY_BINDINGS.forward)) {
      moveInput.add(forward);
    }
    if (this.inputManager.isKeyDown(KEY_BINDINGS.backward)) {
      moveInput.sub(forward);
    }
    if (this.inputManager.isKeyDown(KEY_BINDINGS.left)) {
      moveInput.sub(right);
    }
    if (this.inputManager.isKeyDown(KEY_BINDINGS.right)) {
      moveInput.add(right);
    }
    if (this.inputManager.isKeyDown(KEY_BINDINGS.up)) {
      moveInput.add(up);
    }
    if (this.inputManager.isKeyDown(KEY_BINDINGS.down)) {
      moveInput.sub(up);
    }

    if (moveInput.length() > 0) {
      moveInput.normalize().multiplyScalar(speed * deltaTime);
      this.flyVelocity.lerp(moveInput, 0.2);
    } else {
      this.flyVelocity.lerp(new THREE.Vector3(), 0.1);
    }

    this.camera.position.add(this.flyVelocity);

    // Handle look rotation via drag
    if (input.isDragging && input.primaryPointer) {
      const delta = input.primaryPointer.delta;

      this.flyRotation.y -= delta.x * INPUT_CONFIG.rotationSpeed;
      this.flyRotation.x -= delta.y * INPUT_CONFIG.rotationSpeed;

      // Clamp vertical rotation
      this.flyRotation.x = THREE.MathUtils.clamp(
        this.flyRotation.x,
        -Math.PI / 2 + 0.1,
        Math.PI / 2 - 0.1
      );

      this.camera.quaternion.setFromEuler(this.flyRotation);
    }
  }

  private updateFollowMode(deltaTime: number): void {
    if (!this.config.followTarget) return;

    const targetPos = this.config.followTarget.position.clone();

    // Get behind the target
    const targetDirection = new THREE.Vector3(0, 0, 1);
    this.config.followTarget.getWorldDirection(targetDirection);

    const idealPosition = targetPos
      .clone()
      .sub(targetDirection.multiplyScalar(this.config.followDistance))
      .add(new THREE.Vector3(0, this.config.followDistance * 0.3, 0));

    // Smooth follow
    this.camera.position.lerp(idealPosition, this.config.dampingFactor);
    this.camera.lookAt(targetPos);
  }

  private updateCameraFromSpherical(): void {
    // Convert spherical to Cartesian
    const position = new THREE.Vector3();
    position.setFromSpherical(this.spherical);
    position.add(this.orbitTarget);

    this.camera.position.copy(position);
    this.camera.lookAt(this.orbitTarget);
  }

  // Public API

  public setMode(mode: CameraMode): void {
    this.config.mode = mode;

    if (mode === 'ship') {
      // Reset ship velocity but keep orientation
      this.shipVelocity.set(0, 0, 0);
      this.shipAngularVelocity.set(0, 0, 0);
      this.throttle = 0;
    } else if (mode === 'fly') {
      // Initialize fly rotation from current camera orientation
      this.flyRotation.setFromQuaternion(this.camera.quaternion);
    } else if (mode === 'orbit') {
      // Reset orbit state from current position
      const offset = this.camera.position.clone().sub(this.orbitTarget);
      this.spherical.setFromVector3(offset);
      this.targetZoom = this.spherical.radius;
    }
  }

  public getMode(): CameraMode {
    return this.config.mode;
  }

  public setOrbitTarget(target: THREE.Vector3): void {
    this.orbitTarget.copy(target);
    this.updateCameraFromSpherical();
  }

  public getOrbitTarget(): THREE.Vector3 {
    return this.orbitTarget.clone();
  }

  public setFollowTarget(target: THREE.Object3D | undefined): void {
    this.config.followTarget = target;
  }

  public setDistance(distance: number, immediate: boolean = false): void {
    this.targetZoom = THREE.MathUtils.clamp(
      distance,
      this.config.orbitMinDistance,
      this.config.orbitMaxDistance
    );

    if (immediate) {
      this.spherical.radius = this.targetZoom;
      this.updateCameraFromSpherical();
    }
  }

  public getDistance(): number {
    return this.spherical.radius;
  }

  public lookAt(position: THREE.Vector3): void {
    this.orbitTarget.copy(position);
    this.updateCameraFromSpherical();
  }

  public resize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  public resetView(): void {
    this.orbitTarget.set(0, 0, 0);
    this.spherical.radius = this.config.orbitDistance;
    this.spherical.phi = Math.PI / 3;
    this.spherical.theta = 0;
    this.targetZoom = this.config.orbitDistance;
    this.updateCameraFromSpherical();
  }

  public getShipVelocity(): THREE.Vector3 {
    return this.shipVelocity.clone();
  }

  // Touch control methods
  public setTouchMovement(forward: number, strafe: number): void {
    this.touchMovement.forward = forward;
    this.touchMovement.strafe = strafe;
  }

  public setTouchVertical(vertical: number): void {
    this.touchVertical = vertical;
  }

  public applyTouchLook(deltaX: number, deltaY: number): void {
    this.touchLookDelta.x = deltaX;
    this.touchLookDelta.y = deltaY;
  }
}
